import React from "react";
import { useTranslation } from "utilities/i18n";
import "./Home.scss";
import { HomeCard, Metric, SecondaryMetrics } from "./HomeCard";
import {
  InventoryView,
  useDeviceCountsQuery,
  useInsightsPriorityQuery
} from "generated/graphql";
import { constructFilterList } from "utilities/filters/deviceFilters";
import { GenericBucket, isArrayOfGenericBucket } from "routes/Home/bucketTypes";
import useDeviceAction from "routes/Manage/Actions/useDeviceAction";
import { useHistory, useSelectedTenants } from "polyrouter";
import { getInsightTitleTranslation } from "utilities/insights";
import { findRelativeTimePeriod, tMetric } from "collections/InsightDetails";
import { defaultFilterState } from "routes/Insights/Priority/QueryParams";
import { toInput } from "routes/Insights/Priority/usePriorityInsight";
import { getDataGroupByParam } from "routes/Insights/Priority/PriorityInsights";
import { OptimizelyFeature } from "@optimizely/react-sdk";
import { useSelector } from "react-redux";
import { State } from "../../store";
import { PolyLogoFooter } from "elements";
import { VideoDevices, VoiceDevices } from "./deviceFamilies";
import { usePermissions } from "rbac";
import { useTenantId } from "polyrouter/SelectedTenantProvider";

const DeviceActionsTile: React.FC = () => {
  const { t } = useTranslation();
  const { data, fetching, error } = useDeviceAction();
  if (error) {
    return <></>;
  }

  const [offlineCount, updateCount, siteUnassignedCount] = data.reduce(
    ([offline, update, unassigned], action) => {
      if (action.action === "AVAILABLE_UPDATES") {
        return [offline, update + action.count, unassigned];
      } else if (action.action === "OFFLINE_DEVICES") {
        return [offline + action.count, update, unassigned];
      } else if (action.action === "SITE_UNASSIGNED") {
        return [offline, update, unassigned + action.count];
      } else return [offline, update, unassigned];
    },
    [0, 0, 0]
  );

  const primary = fetching
    ? {
        value: 0,
        label: t("LOADING")
      }
    : {
        value: data.length,
        label: t("ACTIONS")
      };

  const updatesMessage = t("UPDATE_AVAILABLE_COUNT", { count: updateCount });
  const offlineMessage = t("DEVICES_OFFLINE_COUNT", { count: offlineCount });
  const siteUnassignedMessage = t("SITE_UNASSIGNED", {
    count: siteUnassignedCount
  });

  return (
    <HomeCard
      key="actions"
      icon="actions"
      primaryMetric={primary}
      linkTo="/manage/actions"
      data-testid="device-actions-card"
      secondary={[
        {
          kind: "secondary",
          label: offlineCount !== 0 ? offlineMessage : t("NO_DEVICES_OFFLINE")
        },
        {
          kind: "secondary",
          label: updateCount !== 0 ? updatesMessage : t("NO_UPDATES_AVAILABLE")
        },
        {
          kind: "secondary",
          label:
            siteUnassignedCount !== 0
              ? siteUnassignedMessage
              : t("NO_SITE_UNASSIGNED")
        }
      ]}
    />
  );
};

export const InventoryCardLoader: React.FC = () => {
  const tenantId = useTenantId();
  const { data, fetching, error } = useDeviceCountsQuery({
    variables: {
      filter: {
        AND: constructFilterList({}, tenantId)
      },
      aggregations: [
        {
          term: "hardwareModel"
        }
      ]
    }
  });
  if (error) {
    return <></>;
  }
  let devices: Array<GenericBucket> = [];
  if (
    data?.deviceAggregations?.[0]?.buckets &&
    isArrayOfGenericBucket(data.deviceAggregations[0].buckets)
  ) {
    devices = data.deviceAggregations[0].buckets;
  }
  return <InventoryCard devices={devices} loading={fetching} />;
};

export interface HomeProps {
  devices: Array<GenericBucket>;
  loading: boolean;
}

const InventoryCard: React.FC<HomeProps> = ({ devices, loading }) => {
  const { t } = useTranslation();
  const { defaultInventoryView, inventorySessionState } = useSelector(
    (state: State) => {
      return state.userMeta;
    }
  );

  const totalDevices = devices.reduce(
    (count, device) => count + device.count,
    0
  );
  const [videoCount, voiceCount, otherCount] = devices.reduce(
    ([video, voice, other], device) => {
      if (device.__typename === "KeyBucket") {
        const key = device.key.toLowerCase();
        const isVideoDevice = VideoDevices.map(family =>
          key.includes(family)
        ).includes(true);
        const isVoiceDevice = isVideoDevice
          ? false
          : VoiceDevices.map(family => key.includes(family)).includes(true); // only need to check if not a shared device
        const isOther = !isVideoDevice && !isVoiceDevice; // if not either, it's other

        if (isVideoDevice) {
          return [video + device.count, voice, other];
        } else if (isVoiceDevice) {
          return [video, voice + device.count, other];
        } else if (isOther) return [video, voice, other + device.count];
      }
      return [video, voice, other + device.count];
    },
    [0, 0, 0]
  );
  const inventoryProps = loading
    ? {
        value: 0,
        label: t("LOADING")
      }
    : {
        value: totalDevices,
        label: t("INVENTORY")
      };
  let secondary: Array<SecondaryMetrics> = [
    {
      kind: "secondary",
      label:
        videoCount !== 0
          ? t("VIDEO_DEVICES", { videoCount: videoCount.toLocaleString() })
          : t("NO_VIDEO")
    },
    {
      kind: "secondary",
      label:
        voiceCount !== 0
          ? t("VOICE_DEVICES", { voiceCount: voiceCount.toLocaleString() })
          : t("NO_VOICE")
    }
  ];

  if (otherCount) {
    secondary.push({
      kind: "meta",
      value: otherCount,
      label: t("OTHER")
    });
  }

  let routeTo = defaultInventoryView === InventoryView.List ? "table" : "map";
  if (inventorySessionState) {
    routeTo = inventorySessionState === InventoryView.List ? "table" : "map";
  }

  return (
    <HomeCard
      data-testid="inventory-card"
      primaryMetric={inventoryProps}
      secondary={secondary}
      showAll
      linkTo={`/manage/inventory/${routeTo}`}
      icon="inventory"
      key="inventory"
    />
  );
};

export const PriorityInsightsTile: React.FC<{ premium?: boolean }> = ({
  premium = false
}) => {
  const { t } = useTranslation();
  const tM = tMetric();
  const tenants = useSelectedTenants();
  const hasPermission = usePermissions();
  const { data, fetching } = useInsightsPriorityQuery({
    variables: {
      tenant: tenants,
      input: toInput(defaultFilterState, premium)
    },
    pause: !hasPermission("GET_EPISODES")
  });
  const total = data?.topicIndicators.count ?? 0;
  const top = data?.topicIndicators.edges ?? [];
  const empty = top.length === 0;
  const primary: Metric =
    fetching && empty
      ? {
          value: 0,
          label: t("LOADING")
        }
      : {
          value: total,
          label: t("INSIGHTS")
        };
  let secondaryMetric: Array<SecondaryMetrics> = [];
  if (!empty) {
    const first = top[0].node;
    const label = t("PRIORITY_INSIGHT_OVER_TIME", {
      indicator: tM(first.indicator),
      name: getInsightTitleTranslation(first.topic.id),
      timePeriod: findRelativeTimePeriod(
        first.timePeriodStart || "",
        first.timeScope
      ).toLowerCase(),
      scope: getDataGroupByParam(first)
    });

    secondaryMetric = [
      {
        kind: "secondary",
        label
      }
    ];
  }

  return (
    <HomeCard
      key="insights"
      icon="insights"
      primaryMetric={primary}
      linkTo="/insights"
      secondary={secondaryMetric}
    />
  );
};

export const Home: React.FC = () => {
  const history = useHistory();
  const refresh = history.location.state?.refresh;
  if (refresh) {
    let state = { ...history.location.state };
    delete state.refresh;
    history.replace({ ...history.location, state });
    window.location.reload();
    return <></>;
  }

  return (
    <div className="home-content">
      <div className="cards">
        <InventoryCardLoader />
        <DeviceActionsTile />
        <OptimizelyFeature feature="room-insights-categories">
          {stageEnabled => (
            <OptimizelyFeature feature="poly-users-flag">
              {audienceEnabled => {
                if (stageEnabled || audienceEnabled) {
                  return <PriorityInsightsTile premium />;
                }
                return <PriorityInsightsTile />;
              }}
            </OptimizelyFeature>
          )}
        </OptimizelyFeature>
      </div>
      <PolyLogoFooter />
    </div>
  );
};

export default Home;
